import { useState } from "react";
import { adminApi } from "../lib";
import { Panel, In, useAdmin, save, Check, StatusSelect, Thumbs, SampleBadge } from "./parts";

function StoryCard({ s, reload }) {
  const [e, setE] = useState(null);
  const [showOrig, setShowOrig] = useState(false);
  const patch = async (body, msg) => { await save(() => adminApi.patch(`/stories/${s.id}`, body), msg); reload(); };
  const edited = s.original && (s.original.body !== s.body || s.original.title !== s.title);
  return (
    <article className="border border-lou-line p-4" data-testid={`admin-story-${s.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <StatusSelect value={s.status} onChange={(v) => patch({ status: v }, `Marked ${v}`)} testid={`admin-story-status-${s.id}`} />
        <Check label="Featured on home" checked={s.featured} onChange={(v) => patch({ featured: v })} testid={`admin-story-feature-${s.id}`} />
        <span className={`text-xs px-2 py-0.5 ${s.source === "visitor" ? "bg-lou-sage/15 text-lou-sage" : "bg-lou-line"}`}>{s.source === "visitor" ? "Visitor contribution" : "Family"}</span>
        {!s.reviewed && <span className="text-xs bg-lou-clay text-white px-2 py-0.5">new</span>}
        {edited && <span className="text-xs text-lou-soft">edited for display</span>}
        <SampleBadge show={s.is_sample} />
      </div>
      <p className="text-xs text-lou-soft">{s.display_name}{s.relationship ? ` · ${s.relationship}` : ""} · {s.email || "no email"} · chose {s.visibility_choice} · {s.created_at?.slice(0, 10)}</p>
      {e ? (
        <div className="space-y-3 mt-3">
          <In label="Title (public version)" value={e.title} onChange={(v) => setE({ ...e, title: v })} testid="admin-story-title" />
          <In label="Text (public version)" textarea rows={6} value={e.body} onChange={(v) => setE({ ...e, body: v })} testid="admin-story-body" />
          <div className="grid sm:grid-cols-3 gap-3">
            <In label="Display name" value={e.display_name} onChange={(v) => setE({ ...e, display_name: v })} />
            <In label="Approximate date" value={e.date_text} onChange={(v) => setE({ ...e, date_text: v })} />
            <In label="Location" value={e.location} onChange={(v) => setE({ ...e, location: v })} />
          </div>
          <div className="flex gap-3"><button className="lou-btn !py-2 text-sm" onClick={async () => { await patch({ title: e.title, body: e.body, display_name: e.display_name, date_text: e.date_text, location: e.location }); setE(null); }} data-testid="admin-story-save">Save public version</button>
            <button className="lou-btn-ghost text-sm" onClick={() => setE(null)}>Cancel</button></div>
        </div>
      ) : (
        <>
          {s.title && <p className="font-lou text-xl mt-2">{s.title}</p>}
          <p className="text-sm mt-1 whitespace-pre-line line-clamp-6">{s.body}</p>
        </>
      )}
      {s.media_ids?.length > 0 && <div className="mt-3"><Thumbs ids={s.media_ids} /></div>}
      <div className="flex gap-4 mt-3 text-sm">
        {!e && <button className="underline" onClick={() => setE({ ...s })} data-testid={`admin-story-edit-${s.id}`}>Edit public version</button>}
        {s.original?.body && <button className="underline" onClick={() => setShowOrig(!showOrig)} data-testid={`admin-story-original-${s.id}`}>{showOrig ? "Hide" : "View"} original submission</button>}
      </div>
      {showOrig && <div className="mt-3 bg-lou-ivory p-3 text-sm whitespace-pre-line" data-testid={`admin-story-original-text-${s.id}`}><strong>{s.original.title}</strong>{"\n"}{s.original.body}</div>}
    </article>
  );
}

function FamilyStory({ reload }) {
  const [f, setF] = useState(null);
  if (!f) return <button className="lou-btn-ghost text-sm" onClick={() => setF({ display_name: "The family", title: "", body: "", relationship: "" })} data-testid="admin-story-add">Add a family-written memory</button>;
  return (
    <div className="border border-lou-clay/40 bg-lou-paper p-4 space-y-3 mb-6 w-full">
      <div className="grid sm:grid-cols-2 gap-3"><In label="Written by" value={f.display_name} onChange={(v) => setF({ ...f, display_name: v })} />
        <In label="Title" value={f.title} onChange={(v) => setF({ ...f, title: v })} /></div>
      <In label="Memory" textarea rows={5} value={f.body} onChange={(v) => setF({ ...f, body: v })} testid="admin-family-story-body" />
      <div className="flex gap-3"><button className="lou-btn !py-2 text-sm" onClick={async () => { await save(() => adminApi.post("/stories", f), "Memory added"); setF(null); reload(); }} data-testid="admin-family-story-save">Publish</button>
        <button className="lou-btn-ghost text-sm" onClick={() => setF(null)}>Cancel</button></div>
    </div>
  );
}

export default function MemoriesTab() {
  const [filter, setFilter] = useState("");
  const [items, load] = useAdmin(`/stories${filter ? `?status=${filter}` : ""}`);
  return (
    <Panel title="Memories & tributes" testid="admin-memories" actions={<FamilyStory reload={load} />}>
      <p className="text-sm text-lou-soft mb-4">Public contributions appear on the site as soon as they're shared. Use the status to keep one private, reject it, or remove it. The original text is always preserved.</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {["", "approved", "private", "pending", "rejected", "removed"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === s ? "bg-lou-ink text-white" : "border-lou-line"}`} data-testid={`admin-stories-filter-${s || "all"}`}>{s || "all"}</button>
        ))}
      </div>
      <div className="space-y-4">{(items || []).map((s) => <StoryCard key={s.id} s={s} reload={load} />)}</div>
    </Panel>
  );
}

function InboxList({ kind, render, statuses = true }) {
  const [items, load] = useAdmin(`/inbox/${kind}`);
  return (
    <div className="space-y-3" data-testid={`admin-inbox-${kind}`}>
      {items?.length === 0 && <p className="text-sm text-lou-soft">Nothing yet.</p>}
      {(items || []).map((x) => (
        <div key={x.id} className="border border-lou-line p-4 text-sm" data-testid={`admin-inbox-item-${x.id}`}>
          <div className="flex flex-wrap justify-between gap-2">
            <div>{render(x)}</div>
            {statuses && <StatusSelect value={x.status} options={["new", "in_progress", "closed"]} testid={`admin-inbox-status-${x.id}`}
              onChange={async (v) => { await save(() => adminApi.patch(`/inbox/${kind}/${x.id}`, { status: v })); load(); }} />}
          </div>
        </div>
      ))}
    </div>
  );
}

function Identify() {
  const [items, load] = useAdmin("/identify");
  const act = async (id, body, msg) => { await save(() => adminApi.patch(`/identify/${id}`, body), msg); load(); };
  return (
    <div className="space-y-3" data-testid="admin-identify">
      {items?.length === 0 && <p className="text-sm text-lou-soft">No suggestions yet.</p>}
      {(items || []).map((s) => (
        <div key={s.id} className="border border-lou-line p-4 text-sm flex flex-wrap justify-between gap-3" data-testid={`admin-identify-${s.id}`}>
          <div>
            <p>{[s.people && `Pictured: ${s.people}`, s.date_text && `When: ${s.date_text}`, s.location && `Where: ${s.location}`].filter(Boolean).join(" · ") || "—"}</p>
            {s.note && <p className="text-lou-soft">{s.note}</p>}
            <p className="text-xs text-lou-soft">From {s.name} ({s.email}) · photo {s.photo_id.slice(0, 8)} · {s.status}{s.applied ? " · applied" : ""}</p>
          </div>
          <div className="flex gap-2 items-start">
            {!s.applied && <button className="lou-btn !py-1.5 text-xs" onClick={() => act(s.id, { apply: true }, "Added to the photograph record")} data-testid={`admin-identify-apply-${s.id}`}>Apply to record</button>}
            <StatusSelect value={s.status} onChange={(v) => act(s.id, { status: v }, `Marked ${v}`)} />
          </div>
        </div>
      ))}
    </div>
  );
}

const FRAMES = { unframed: "Unframed", interested: "Interested in framing", undecided: "Undecided" };
const PrintRow = ({ x }) => (
  <div data-testid={`admin-print-${x.id}`}>
    <p className="font-medium">{x.artwork_title} <span className="text-lou-soft font-normal">· {x.artwork_ref || "—"}</span>
      {x.artwork_url && <a href={x.artwork_url} target="_blank" rel="noreferrer" className="underline ml-2 text-xs">view painting</a>}</p>
    <p>{x.name} · {x.email}{x.phone ? ` · ${x.phone}` : ""}</p>
    <p className="text-lou-soft">Size: {x.size || "—"} · Paper: {x.paper || "—"} · Framing: {FRAMES[x.framing] || "—"} · Qty {x.quantity}</p>
    {[x.city, x.region, x.postal_code, x.country].some(Boolean) && <p className="text-lou-soft">Deliver to: {[x.city, x.region, x.postal_code, x.country].filter(Boolean).join(", ")}</p>}
    {x.message && <p className="text-lou-soft mt-1">{x.message}</p>}
  </div>
);

export function InboxTab() {
  const [tab, setTab] = useState("prints");
  const TABS = [["prints", "Print inquiries"], ["identify", "Photo identifications"], ["corrections", "Corrections & removals"], ["rsvps", "RSVPs"]];
  return (
    <Panel title="Inbox" testid="admin-inbox">
      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map(([k, t]) => <button key={k} onClick={() => setTab(k)} className={`text-sm px-3 py-1.5 rounded-full border ${tab === k ? "bg-lou-ink text-white" : "border-lou-line"}`} data-testid={`admin-inbox-tab-${k}`}>{t}</button>)}
      </div>
      {tab === "prints" && <InboxList kind="prints" render={(x) => <PrintRow x={x} />} />}
      {tab === "corrections" && <InboxList kind="corrections" render={(x) => <><p className="font-medium capitalize">{x.request_type}: {x.target || "—"}</p><p>{x.name} · {x.email}</p><p className="text-lou-soft mt-1">{x.message}</p></>} />}
      {tab === "rsvps" && <InboxList kind="rsvps" statuses={false} render={(x) => <><p className="font-medium">{x.name} — {x.guests} attending {x.event ? `· ${x.event}` : ""}</p><p>{x.email}</p>{x.message && <p className="text-lou-soft">{x.message}</p>}</>} />}
      {tab === "identify" && <Identify />}
    </Panel>
  );
}
